import type { ShopBuilderDocument, ShopBuilderEditorNode } from "./builderDocument";
import type { ShopRichTextMark, ShopTemplateBlock } from "./types";

export type ShopPaletteColors = {
    accent: string;
    background: string;
    border: string;
    muted: string;
    primary: string;
    surface: string;
    text: string;
};

type BasePaletteRole = keyof ShopPaletteColors;
type ThemePaletteRole = BasePaletteRole
    | "accentSoft"
    | "borderStrong"
    | "focusRing"
    | "inverse"
    | "onAccent"
    | "onBackground"
    | "onInverse"
    | "onPrimary"
    | "onSurface"
    | "overlay"
    | "primarySoft"
    | "shadowColor"
    | "surfaceElevated";

type RgbColor = { alpha: number; blue: number; green: number; red: number };

const basePaletteRoles: BasePaletteRole[] = [
    "primary",
    "accent",
    "background",
    "surface",
    "text",
    "muted",
    "border",
];

const semanticVariableRoles: Record<string, ThemePaletteRole> = {
    "--site-accent": "accent",
    "--site-accent-soft": "accentSoft",
    "--site-background": "background",
    "--site-border": "border",
    "--site-border-strong": "borderStrong",
    "--site-focus-ring": "focusRing",
    "--site-inverse": "inverse",
    "--site-muted": "muted",
    "--site-on-accent": "onAccent",
    "--site-on-background": "onBackground",
    "--site-on-inverse": "onInverse",
    "--site-on-primary": "onPrimary",
    "--site-on-surface": "onSurface",
    "--site-overlay": "overlay",
    "--site-primary": "primary",
    "--site-primary-soft": "primarySoft",
    "--site-shadow-color": "shadowColor",
    "--site-surface": "surface",
    "--site-surface-elevated": "surfaceElevated",
    "--site-text": "text",
};

const preservedColorValues = new Set([
    "",
    "currentcolor",
    "inherit",
    "initial",
    "none",
    "transparent",
    "unset",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function expandedHex(value: string): string | null {
    const match = value.trim().match(/^#([\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i);

    if (!match) return null;

    const raw = match[1];

    return raw.length === 3 || raw.length === 4
        ? raw.split("").map((character) => `${character}${character}`).join("")
        : raw;
}

function parseCssColor(value: string): RgbColor | null {
    const normalized = value.trim().toLowerCase();
    const named = normalized === "white"
        ? "#ffffff"
        : normalized === "black"
            ? "#000000"
            : value;
    const hex = expandedHex(named);

    if (hex) {
        return {
            red: Number.parseInt(hex.slice(0, 2), 16),
            green: Number.parseInt(hex.slice(2, 4), 16),
            blue: Number.parseInt(hex.slice(4, 6), 16),
            alpha: hex.length === 8 ? Number.parseInt(hex.slice(6, 8), 16) / 255 : 1,
        };
    }

    const rgb = normalized.match(
        /^rgba?\(\s*([\d.]+)(%)?\s*[, ]\s*([\d.]+)(%)?\s*[, ]\s*([\d.]+)(%)?(?:\s*[,/]\s*([\d.]+)(%)?)?\s*\)$/i,
    );

    if (!rgb) return null;

    const component = (raw: string, percentage: boolean) => (
        Math.max(0, Math.min(255, Number(raw) * (percentage ? 2.55 : 1)))
    );
    const alphaRaw = rgb[7];
    const alpha = alphaRaw === undefined
        ? 1
        : Math.max(0, Math.min(1, Number(alphaRaw) * (rgb[8] ? 0.01 : 1)));

    return {
        red: component(rgb[1], Boolean(rgb[2])),
        green: component(rgb[3], Boolean(rgb[4])),
        blue: component(rgb[5], Boolean(rgb[6])),
        alpha,
    };
}

function colorDistance(left: RgbColor, right: RgbColor): number {
    const redMean = (left.red + right.red) / 2;
    const red = left.red - right.red;
    const green = left.green - right.green;
    const blue = left.blue - right.blue;

    return ((2 + redMean / 256) * red * red)
        + (4 * green * green)
        + ((2 + (255 - redMean) / 256) * blue * blue);
}

function relativeLuminance(color: RgbColor): number {
    const component = (value: number) => {
        const normalized = value / 255;

        return normalized <= 0.04045
            ? normalized / 12.92
            : ((normalized + 0.055) / 1.055) ** 2.4;
    };

    return (0.2126 * component(color.red))
        + (0.7152 * component(color.green))
        + (0.0722 * component(color.blue));
}

function contrastRatio(left: RgbColor, right: RgbColor): number {
    const leftLuminance = relativeLuminance(left);
    const rightLuminance = relativeLuminance(right);

    return (Math.max(leftLuminance, rightLuminance) + 0.05)
        / (Math.min(leftLuminance, rightLuminance) + 0.05);
}

function bestPaletteContrast(background: string, colors: ShopPaletteColors): string {
    const parsedBackground = parseCssColor(background);
    const candidates = [colors.text, colors.background, colors.surface, colors.primary, colors.accent]
        .filter((value, index, values) => values.indexOf(value) === index);

    if (!parsedBackground) return colors.text;

    return candidates.reduce((best, candidate) => {
        const parsedCandidate = parseCssColor(candidate);
        const parsedBest = parseCssColor(best);

        if (!parsedCandidate) return best;
        if (!parsedBest) return candidate;

        return contrastRatio(parsedBackground, parsedCandidate) > contrastRatio(parsedBackground, parsedBest)
            ? candidate
            : best;
    }, candidates[0] ?? colors.text);
}

function themeRoleColor(role: ThemePaletteRole, colors: ShopPaletteColors): string {
    if (role in colors) return colors[role as BasePaletteRole];

    if (role === "onPrimary") return bestPaletteContrast(colors.primary, colors);
    if (role === "onAccent") return bestPaletteContrast(colors.accent, colors);
    if (role === "onBackground") return bestPaletteContrast(colors.background, colors);
    if (role === "onSurface") return bestPaletteContrast(colors.surface, colors);
    if (role === "inverse") return colors.text;
    if (role === "onInverse") return bestPaletteContrast(colors.text, colors);
    if (role === "primarySoft" || role === "accentSoft" || role === "surfaceElevated") return colors.surface;
    if (role === "borderStrong") return colors.border;
    if (role === "focusRing") return colors.primary;
    if (role === "overlay") return `color-mix(in srgb, ${colors.text} 48%, transparent)`;

    return `color-mix(in srgb, ${colors.text} 18%, transparent)`;
}

function semanticRole(value: string): ThemePaletteRole | null {
    const match = value.trim().match(/^var\(\s*(--site-[\w-]+)\s*\)$/i);

    return match ? semanticVariableRoles[match[1].toLowerCase()] ?? null : null;
}

function contextualRoles(key: string): BasePaletteRole[] {
    const normalized = key.toLowerCase();

    if (normalized.includes("border") || normalized.includes("track")) {
        return ["border", "primary", "accent", "muted", "text"];
    }

    if (normalized.includes("fill") || normalized.includes("shape") || normalized.includes("dataset")) {
        return ["primary", "accent", "text", "muted", "border"];
    }

    if (normalized.includes("background")) {
        return ["background", "surface", "primary", "accent", "text", "muted"];
    }

    return ["text", "muted", "primary", "accent", "background", "surface", "border"];
}

function nearestPaletteRole(value: RgbColor, key: string, colors: ShopPaletteColors): BasePaletteRole {
    const candidates = contextualRoles(key);

    return candidates.reduce((best, role) => {
        const candidate = parseCssColor(colors[role]);
        const currentBest = parseCssColor(colors[best]);

        if (!candidate) return best;
        if (!currentBest) return role;

        return colorDistance(value, candidate) < colorDistance(value, currentBest) ? role : best;
    }, candidates[0]);
}

function rethemedParsedColor(
    parsed: RgbColor,
    key: string,
    fromColors: ShopPaletteColors,
    nextColors: ShopPaletteColors,
): string {
    const role = nearestPaletteRole(parsed, key, fromColors);
    const color = nextColors[role];

    return parsed.alpha < 0.999
        ? `color-mix(in srgb, ${color} ${Math.round(parsed.alpha * 100)}%, transparent)`
        : color;
}

const embeddedColorPattern = /#[\da-f]{3,8}\b|rgba?\([^)]*\)|\b(?:black|white)\b/gi;

function rethemeEmbeddedColors(
    value: string,
    key: string,
    fromColors: ShopPaletteColors,
    nextColors: ShopPaletteColors,
): string {
    return value.replace(embeddedColorPattern, (candidate) => {
        const parsed = parseCssColor(candidate);

        return parsed ? rethemedParsedColor(parsed, key, fromColors, nextColors) : candidate;
    });
}

function rethemeCssValuePreservingUrls(
    value: string,
    key: string,
    fromColors: ShopPaletteColors,
    nextColors: ShopPaletteColors,
): string {
    const urls: string[] = [];
    const protectedValue = value.replace(/url\((?:"[^"]*"|'[^']*'|[^)])*\)/gi, (url) => {
        const token = `__shop_palette_url_${urls.length}__`;

        urls.push(url);

        return token;
    });
    const themed = rethemeEmbeddedColors(protectedValue, key, fromColors, nextColors);

    return themed.replace(/__shop_palette_url_(\d+)__/g, (_token, index: string) => (
        urls[Number(index)] ?? _token
    ));
}

function rethemeColorValue(
    value: unknown,
    key: string,
    fromColors: ShopPaletteColors,
    nextColors: ShopPaletteColors,
): unknown {
    if (typeof value !== "string") return value;

    const normalized = value.trim().toLowerCase();

    if (preservedColorValues.has(normalized)) return value;

    const role = semanticRole(value);

    if (role) return themeRoleColor(role, nextColors);

    const parsed = parseCssColor(value);

    if (parsed) return rethemedParsedColor(parsed, key, fromColors, nextColors);

    if (
        value.includes("gradient(")
        || value.includes("color-mix(")
        || key.toLowerCase().includes("shadow")
    ) {
        return rethemeCssValuePreservingUrls(value, key, fromColors, nextColors);
    }

    if (/^(?:hsl|hsla|hwb|lab|lch|oklab|oklch|color)\(/i.test(normalized)) {
        return nextColors[contextualRoles(key)[0]];
    }

    return value;
}

function isPlainColorKey(key: string): boolean {
    const normalized = key.toLowerCase();

    return normalized === "color"
        || normalized.endsWith("color")
        || normalized === "chartdatasetborder";
}

function isEmbeddedColorKey(key: string): boolean {
    const normalized = key.toLowerCase();

    return normalized === "boxshadow"
        || normalized.endsWith("boxshadow")
        || normalized === "backgroundimage"
        || normalized.endsWith("backgroundimage");
}

function rethemeChartSeries(
    value: unknown,
    fromColors: ShopPaletteColors,
    nextColors: ShopPaletteColors,
): unknown {
    const parsed = typeof value === "string" ? (() => {
        try {
            return JSON.parse(value) as unknown;
        } catch {
            return null;
        }
    })() : value;

    if (!Array.isArray(parsed)) return value;

    const next = parsed.map((item) => {
        if (!isRecord(item)) return item;

        return Object.fromEntries(Object.entries(item).map(([key, itemValue]) => (
            key === "color" || key === "borderColor"
                ? [key, rethemeColorValue(itemValue, key, fromColors, nextColors)]
                : [key, itemValue]
        )));
    });

    return typeof value === "string" ? JSON.stringify(next) : next;
}

function rethemeOptions(
    options: Record<string, unknown> | undefined,
    fromColors: ShopPaletteColors,
    nextColors: ShopPaletteColors,
): Record<string, unknown> | undefined {
    if (!options) return undefined;

    return Object.fromEntries(Object.entries(options).map(([key, value]) => {
        if (key === "socialIconBrandColor" || key === "shareColored") return [key, "false"];

        if (key === "builderStyleSnapshot" && isRecord(value)) {
            return [key, { ...value, colors: { ...nextColors } }];
        }

        if (key === "chartSeries") return [key, rethemeChartSeries(value, fromColors, nextColors)];

        if (isPlainColorKey(key) || isEmbeddedColorKey(key)) {
            return [key, rethemeColorValue(value, key, fromColors, nextColors)];
        }

        return [key, value];
    }));
}

function rethemeRichTextMarks(
    marks: ShopRichTextMark | undefined,
    fromColors: ShopPaletteColors,
    nextColors: ShopPaletteColors,
): ShopRichTextMark | undefined {
    if (!marks?.textColor) return marks;

    return {
        ...marks,
        textColor: String(rethemeColorValue(marks.textColor, "textColor", fromColors, nextColors)),
    };
}

function rethemeBlock(
    block: ShopTemplateBlock,
    fromColors: ShopPaletteColors,
    nextColors: ShopPaletteColors,
): ShopTemplateBlock {
    const content = block.content
        ? Object.fromEntries(Object.entries(block.content).map(([key, value]) => (
            key === "chartSeries"
                ? [key, rethemeChartSeries(value, fromColors, nextColors)]
                : [key, value]
        )))
        : undefined;
    const richText = block.richText
        ? Object.fromEntries(Object.entries(block.richText).map(([field, segments]) => [
            field,
            segments.map((segment) => ({
                ...segment,
                ...(segment.marks ? { marks: rethemeRichTextMarks(segment.marks, fromColors, nextColors) } : {}),
            })),
        ]))
        : undefined;
    const slots = block.slots
        ? Object.fromEntries(Object.entries(block.slots).map(([slot, children]) => [
            slot,
            children.map((child) => rethemeBlock(child, fromColors, nextColors)),
        ]))
        : undefined;
    const children = block.children?.map((child) => rethemeBlock(child, fromColors, nextColors));

    return {
        ...block,
        ...(block.options ? { options: rethemeOptions(block.options, fromColors, nextColors) } : {}),
        ...(content ? { content } : {}),
        ...(richText ? { richText } : {}),
        ...(slots ? { slots } : {}),
        ...(children ? { children } : {}),
    };
}

export function rethemeShopBlocks(
    blocks: readonly ShopTemplateBlock[],
    fromColors: ShopPaletteColors,
    nextColors: ShopPaletteColors,
): ShopTemplateBlock[] {
    return blocks.map((block) => rethemeBlock(block, fromColors, nextColors));
}

function rethemeEditorNode(
    node: ShopBuilderEditorNode,
    fromColors: ShopPaletteColors,
    nextColors: ShopPaletteColors,
): ShopBuilderEditorNode {
    return {
        ...node,
        ...(node.attrs?.textColor ? {
            attrs: {
                ...node.attrs,
                textColor: rethemeColorValue(node.attrs.textColor, "textColor", fromColors, nextColors),
            },
        } : {}),
        ...(node.marks ? {
            marks: node.marks.map((mark) => ({
                ...mark,
                ...(mark.attrs?.textColor ? {
                    attrs: {
                        ...mark.attrs,
                        textColor: rethemeColorValue(mark.attrs.textColor, "textColor", fromColors, nextColors),
                    },
                } : {}),
            })),
        } : {}),
        ...(node.content ? { content: node.content.map((child) => rethemeEditorNode(child, fromColors, nextColors)) } : {}),
    };
}

export function rethemeShopBuilderDocument(
    document: ShopBuilderDocument,
    fromColors: ShopPaletteColors,
    nextColors: ShopPaletteColors,
): ShopBuilderDocument {
    return {
        ...document,
        sections: document.sections.map((section) => ({
            ...section,
            block: rethemeBlock(section.block, fromColors, nextColors),
            zones: section.zones.map((zone) => ({
                ...zone,
                ...(zone.blocks ? { blocks: rethemeShopBlocks(zone.blocks, fromColors, nextColors) } : {}),
                ...(zone.editor ? { editor: rethemeEditorNode(zone.editor, fromColors, nextColors) as typeof zone.editor } : {}),
            })),
        })),
    };
}
